<template>
  <BaseWidget
    :title="content.title"
    freeMode
    :widgetId="content.id"
    :edit="edit"
    :isWidgetLoading="isWidgetLoading"
    :content="content"
    :getWidgetReport="getWidgetReport"
  >
    <template v-slot:widget-control>
      <span v-if="!edit && trendCalculationMethod" class="summary-widget__trend">{{ trendCalculationMethod }}</span>
    </template>

    <Summary
      v-model="metrics"
      @input="onUpdateMetrics"
      @onOrderChange="onOrderChange"
      :items="itemsFiltered"
      :previous-items="previousItems"
      class="summary-widget"
      :module="EXPLORE_MODULE"
      :is-edit-mode="edit"
      :is-widget-loading="isWidgetLoading"
      :show-trends="content.settings.showTrends"
      :use-compact-format="content.settings.compactFormat"
      :trend-calculation="content.settings.trendCalculation"
      :are-badges-loading="areBadgesLoading"
    />
  </BaseWidget>
</template>

<script lang="ts">
import { format, subYears } from "date-fns";
import { cloneDeep, omit } from "lodash";
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";

import Summary from "@/components/summary/Summary.vue";
import BaseWidget from "@/components/widgets/explore/BaseWidget.vue";
import { EXPLORE_MODULE } from "@/constants";
import csvExport, { buildFileName, buildFilterRows, buildHeaderRows } from "@/helpers/csvExport";
import { setDate, setPreviousDate } from "@/helpers/dateRangePicker";
import { DATE_FORMAT, stringOrNumberToDate } from "@/helpers/formatters";
import Lang from "@/lang";
import { widgetApi } from "@/router";
import inputsDictionary from "@/settings/inputsDictionary";
import { metricsDictionary } from "@/settings/metricsDictionary";
import { SummaryItem, TrendColor, trendColors, trendDirections } from "@/settings/summary/summary";
import { Dashboard, FilterParams } from "@/store/modules/userSettings";
import { ContentWidget } from "@/types/layouts";

export default Vue.extend({
  name: "SummaryWidget",

  props: {
    content: Object as () => ContentWidget,
    value: Object,
    edit: Boolean,
  },

  components: {
    Summary,
    BaseWidget,
  },

  beforeMount() {
    this.getData();
  },

  data() {
    return {
      EXPLORE_MODULE,
      Lang,
      items: [] as SummaryItem[],
      previousItems: [] as SummaryItem[],
      metrics: (this.content?.settings?.metrics || []) as string[],
      isWidgetLoading: false,
      areBadgesLoading: false,
    };
  },

  computed: {
    ...mapGetters("userSettings", ["dashboards", "filterParams", "filterParams"]),

    currentDashboard(): Dashboard {
      return this.dashboards?.find((dashboard: Dashboard) => {
        return this.$route.params?.dashboardName === dashboard.id;
      });
    },

    dashboardName(): string {
      return this.$route.params?.dashboardName;
    },

    itemsFiltered(): SummaryItem[] {
      return (this.metrics as string[]).map((metric: string) => {
        return (
          this.items.find((item) => {
            return metric === item.name;
          }) || { name: metric, value: null }
        );
      });
    },

    trendCalculationMethod(): string | null {
      return (
        inputsDictionary.summaryTrendOptions.find((item) => item.value === this.content.settings.trendCalculation)
          ?.title || null
      );
    },

    mappedFilterParams(): FilterParams {
      if (this.filterParams?.dateRange.type !== "preset" || this.filterParams.dateRange.value?.preset === "allTime") {
        return {
          ...cloneDeep(this.filterParams),
        };
      }

      let startTime, endTime;

      if (this.filterParams?.dateRange.type === "preset") {
        const { startDate, endDate } = setDate(this.filterParams.dateRange.value?.preset as string);
        startTime = startDate;
        endTime = endDate;
      }

      if (this.content?.settings?.trendCalculation === "pop") {
        const previousDate = setPreviousDate(this.filterParams.dateRange.value?.preset);

        startTime = previousDate.startDate;
        endTime = previousDate.endDate;
      }

      if (this.content?.settings?.trendCalculation === "yoy") {
        let currentDate = setDate(this.filterParams.dateRange.value?.preset);

        startTime = format(subYears(stringOrNumberToDate(currentDate.startDate), 1), DATE_FORMAT);
        endTime = format(subYears(stringOrNumberToDate(currentDate.endDate), 1), DATE_FORMAT);
      }

      return {
        ...cloneDeep(this.filterParams),
        dateRange: {
          type: "basic",
          value: {
            startTime,
            endTime,
          },
        },
      };
    },
  },

  methods: {
    ...mapActions("userSettings", ["updatePage"]),

    async getData(): Promise<void> {
      this.isWidgetLoading = true;
      await this.getCurrentData();
      this.isWidgetLoading = false;

      if (this.edit || !this.content.settings.showTrends) {
        return;
      }

      this.areBadgesLoading = true;
      await this.getPreviousData();
      this.showBadges();
      this.areBadgesLoading = false;
    },

    async getCurrentData(): Promise<void> {
      const data = await widgetApi.getSummaryData(this.metrics);
      if (!data?.length) {
        return;
      }
      const isBadgeDisplayed =
        this.filterParams?.dateRange.type === "preset" &&
        this.shouldPresetBeDisplayed(this.filterParams?.dateRange.value?.preset);

      this.items = Object.keys(data[0]).map((key) => {
        const dictionaryItem = inputsDictionary.displayMetrics.find((item) => item.value === key);

        return {
          name: key,
          value: data[0][key] ?? 0,
          badge: {
            isShown: isBadgeDisplayed && (dictionaryItem?.showTrend || false),
          },
        };
      });
    },

    async getPreviousData(): Promise<void> {
      const areAllMetricsBadgesDisabled = !this.metrics.filter(
        (metric) => !!inputsDictionary.displayMetrics.find((item) => item.value === metric)?.showTrend
      ).length;

      if (areAllMetricsBadgesDisabled) {
        this.previousItems = [];
        return;
      }

      const data = await widgetApi.getSummaryData(this.metrics, this.mappedFilterParams);

      if (!data?.length) {
        return;
      }

      this.previousItems = Object.keys(data[0]).map((key) => ({
        name: key,
        value: data[0][key] ?? 0,
      }));
    },

    showBadges(): void {
      const getOppositeColor = (color: TrendColor | null) =>
        color ? (color === trendColors.positive ? trendColors.negative : trendColors.positive) : null;

      this.items = this.items.map((item) => {
        const previousItemValue = (this.previousItems.find((previousItem) => previousItem.name === item.name)?.value ??
          0) as number;
        const itemsDifference: number = (item.value as number) - previousItemValue;
        const dictionaryItem = inputsDictionary.displayMetrics.find((metricItem) => metricItem.value === item.name);

        return {
          ...item,
          badge: {
            direction:
              itemsDifference === 0
                ? trendDirections.same
                : itemsDifference > 0
                ? trendDirections.up
                : trendDirections.down,
            color:
              itemsDifference === 0
                ? trendColors.default
                : (itemsDifference > 0
                    ? dictionaryItem?.trendUpColor
                    : getOppositeColor(dictionaryItem?.trendUpColor || null)) || trendColors.default,
            isShown: item.badge?.isShown as boolean,
          },
        };
      });
    },

    shouldPresetBeDisplayed(preset: string) {
      const isPopEnabled = this.content?.settings?.trendCalculation === "pop";

      switch (preset) {
        case "thisYear":
          return !isPopEnabled;
        case "allTime":
          return false;
        default:
          return true;
      }
    },

    onUpdateMetrics(updatedMetrics: string[], doDataRefresh: boolean): void {
      this.$emit("inputSummary", {
        ...this.value,
        settings: { ...this.content?.settings, metrics: updatedMetrics },
      });
      if (doDataRefresh) {
        this.getData();
      }
    },

    async onSave(): Promise<void> {
      await this.updatePage(this.currentDashboard);
    },

    getWidgetReport(isSingleChart = true): void {
      const headers = ["Metric", "Value", "Previous period value", "Trend"];
      const preparedItems = this.items
        .filter((item) => this.metrics.includes(item.name))
        .map((item) => [
          metricsDictionary.find((metric) => metric.value === item.name)?.title,
          item.value,
          this.previousItems.find((prevItem) => prevItem.name === item.name)?.value || null,
          item.badge?.direction,
        ]);
      let body = widgetApi.getSummaryRequestBody(this.metrics);
      body = omit(body, ["count_metrics"]);

      const csvContent = [];
      csvContent.push(...buildHeaderRows(this.content?.title));
      csvContent.push(...buildFilterRows(body, isSingleChart));

      csvContent.push([]);
      csvContent.push(headers);
      csvContent.push(...preparedItems);

      const fileName = buildFileName(this.content?.title);

      csvExport(csvContent, fileName);
    },

    onOrderChange({ newIndex, oldIndex }: Record<string, number>) {
      const movedElement = this.metrics.splice(oldIndex, 1);
      this.metrics.splice(newIndex, 0, movedElement[0]);
    },
  },

  watch: {
    dashboardName(): void {
      this.getData();
    },

    filterParams: {
      deep: true,
      async handler(): Promise<void> {
        await this.getData();
      },
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/theme.scss";
@import "@/scss/custom_variables.scss";

.summary-widget {
  &__trend {
    font-size: 14px;
    line-height: 32px;
    color: var(--v-content-medium-base);
    text-transform: uppercase;
  }
  &__edit-title {
    font-size: 20px;
    font-weight: 600;
    line-height: 32px;
    margin-bottom: $space-medium;

    &::before {
      content: "";
      display: inline-block;
      height: 32px;
      width: 8px;
      margin-right: 16px;
      vertical-align: bottom;
      background-color: var(--v-secondary-base);
      border-radius: 4px;
    }
  }

  &__btn-block {
    display: flex;
    justify-content: flex-end;
    padding-top: $space-medium;
  }
}
</style>
